import { PropsWithChildren, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { Box, Grid } from "styled-system/jsx";
import {
  ErrorBoundaryPage,
  GlobalRouteSearchLauncher,
  ImpersonationBanner,
  LoadingPage,
  MaintenanceNoticeBanner,
  MaintenancePage,
  RootHeader,
  SandboxBanner,
  ScreenSizeWarning,
  WorkbenchSessionBanner,
} from "shared/components";
import {
  EnumUserStatus,
  EnumUserUserType,
  useUserUpdateMutation,
} from "gql/generated";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppContext } from "useAppContext";
import { useImpersonation } from "shared/contextProviders/impersonation";
import { createMultilingual } from "shared/utils";
import { useMaintenance } from "./useMaintenance.ts";
import { useSandbox } from "./useSandbox.ts";
import { NotAuthorized } from "../NotAuthorized";
import { PostHogErrorBoundary } from "posthog-js/react";
import { usePostHog } from "shared/hooks";

export const RootLayout = ({ children }: PropsWithChildren) => {
  const { user } = useAuth0();
  const [searchParams] = useSearchParams();
  const { isAuthorized, dbUser, isForcingLogout, refetchDbUser } =
    useAppContext();
  const [updateUserStatus] = useUserUpdateMutation();
  const {
    maintenanceData,
    maintenanceLoading,
    maintenanceError,
    showMaintenanceBanner,
    showMaintenanceDoorstop,
    handleClose,
    getHashMessage,
  } = useMaintenance();
  const {
    showSandboxBanner,
    organizationId: sandboxOrgId,
    handleClose: handleSandboxClose,
  } = useSandbox();
  const { isImpersonating } = useImpersonation();
  const { getPostHogErrorCaptureData, trackPostHogException } = usePostHog();
  const [isGlobalRouteSearchOpen, setIsGlobalRouteSearchOpen] = useState(false);
  const demoErrorBoundary = searchParams.has("demoErrorBoundary");
  const showGlobalRouteSearch =
    dbUser?.userType === EnumUserUserType.TnbInternal;

  useEffect(() => {
    if (!showGlobalRouteSearch) {
      setIsGlobalRouteSearchOpen(false);
      return;
    }

    const isTypingTarget = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName.toLowerCase();
      return (
        target.isContentEditable ||
        tag === "input" ||
        tag === "textarea" ||
        tag === "select"
      );
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      if (!event.altKey) return;
      if (event.code !== "KeyW") return;
      event.preventDefault();
      setIsGlobalRouteSearchOpen(true);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showGlobalRouteSearch]);

  // Ensure the user is updated correctly (skip during impersonation to avoid mutating the impersonated user)
  useEffect(() => {
    if (isImpersonating) return;

    const checkUser = async () => {
      try {
        if (dbUser && dbUser._id && dbUser.status !== EnumUserStatus.Enrolled) {
          await updateUserStatus({
            variables: {
              id: dbUser._id,
              record: {
                status: EnumUserStatus.Enrolled,
              },
            },
          });
          await refetchDbUser();
        }
      } catch (error) {
        console.error(error);
        trackPostHogException(error, {
          fileName: "RootLayout.tsx",
          method: "checkUser",
          message: "Error checking user",
        });
      }
    };
    checkUser();
  }, [dbUser, user, isImpersonating, refetchDbUser, updateUserStatus, trackPostHogException]);

  if (isForcingLogout || maintenanceLoading) {
    return (
      <Grid height="svh" width="full" backgroundColor={"bg.wa.lightest"}>
        <LoadingPage />
      </Grid>
    );
  }

  if (maintenanceError) {
    return (
      <Grid height="svh" width="full" backgroundColor={"bg.wa.lightest"}>
        <ErrorBoundaryPage />
      </Grid>
    );
  }

  if (showMaintenanceDoorstop) {
    return (
      <Grid height="svh" width="full" backgroundColor={"bg.wa.lightest"}>
        <MaintenancePage
          message={
            maintenanceData?.maintenanceFindOne?.doorstop.message ??
            createMultilingual("")
          }
        />
      </Grid>
    );
  }

  if (!isAuthorized) {
    return <NotAuthorized />;
  }

  if (demoErrorBoundary) {
    return (
      <Grid height="svh" width="full" backgroundColor={"bg.wa.lightest"}>
        <ErrorBoundaryPage />
      </Grid>
    );
  }

  return (
    <Grid
      gridTemplateRows={
        showMaintenanceBanner ? "64px auto 73px 1fr" : "0 auto 73px 1fr"
      }
      height={"svh"}
      backgroundColor={"bg.wa.lightest"}
      rowGap={0}
      gridRowGap={0}
      gap={0}
    >
      <MaintenanceNoticeBanner
        message={
          maintenanceData?.maintenanceFindOne?.banner.message ??
          createMultilingual("")
        }
        hashMessage={getHashMessage()}
        open={showMaintenanceBanner}
        onClose={handleClose}
      />
      <Box>
        <ScreenSizeWarning />
        <ImpersonationBanner />
        <WorkbenchSessionBanner />
        <SandboxBanner
          organizationId={sandboxOrgId}
          open={showSandboxBanner}
          onClose={handleSandboxClose}
        />
      </Box>
      <RootHeader
        showGlobalRouteSearchTrigger={showGlobalRouteSearch}
        onOpenGlobalRouteSearch={() => setIsGlobalRouteSearchOpen(true)}
      />
      <PostHogErrorBoundary
        fallback={<ErrorBoundaryPage />}
        additionalProperties={getPostHogErrorCaptureData({
          fileName: "RootLayout.tsx",
          extra: {
            errorBoundaryMessage:
              "RootLayout error boundary ... full-screen <ErrorBoundaryPage /> displayed",
          },
        })}
      >
        <Box overflow={"hidden"}>{children}</Box>
      </PostHogErrorBoundary>
      {showGlobalRouteSearch && (
        <GlobalRouteSearchLauncher
          isOpen={isGlobalRouteSearchOpen}
          onClose={() => setIsGlobalRouteSearchOpen(false)}
        />
      )}
    </Grid>
  );
};
